/**
 * Aba "Análise Comportamental" da Tela A2 (Config Tenant) — ADR-032.
 *
 * Expõe os campos REAIS do `tenant-config` (R15): `behavioralDetectionEnabled`
 * (master switch) + `behavioralMonitoredPatternTypes` (subset dos 8 tipos
 * canônicos a monitorar). O motor de detecção (`BehavioralPatternDetectionService`
 * + cron) já existe; esta aba dá ao admin a config por-tenant. Escrita gated
 * em `tenant-config-manage`.
 *
 * Semântica do subset (future-proof): se TODOS os tipos estão marcados, salvamos
 * `null` (= "todos", incluindo tipos futuros). Subset → array explícito.
 *
 * Admin Hub T8 — os botões locais Descartar/Salvar saíram; a seção registra na
 * `StickySaveBar` do domínio via `useSectionSave` (mesmo movimento do `GeneralTab`,
 * T7). Fix round 1: o `<header>` próprio (título + "sinaliza ao supervisor")
 * SAIU — mesmo movimento que a T7 já tinha feito em `GeneralTab`. A primeira
 * versão desta task tinha mantido o header por o `item.efeito` do catálogo não
 * cobrir "sinaliza ao supervisor"; a correção agora é dobrar essa frase no
 * PRÓPRIO `item.efeito` (`catalog.ts`) em vez de manter um segundo título —
 * um lugar só por descrição. Ver task-8-report.md (fix round 1).
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { useSectionSave } from '../hub/save-bar-context';

import { detectionStatusCopy } from './behavioral-copy';
import { BEHAVIORAL_TAB_FIELD_LABELS } from './config-tenant-labels';
import { useTenantConfig, useUpdateTenantConfig } from './use-tenant-config';

import type {
  BehavioralPatternTypeCode,
  SerializedTenantConfig,
  UpdateTenantConfigRequest,
} from './types';

/** 8 tipos canônicos + label PT (alinhado ao `humanizePatternType` do Supervisor). */
const PATTERN_TYPES: readonly { code: BehavioralPatternTypeCode; label: string }[] = [
  { code: 'defensive_tone', label: 'Tom defensivo' },
  { code: 'interrupting', label: 'Interrupções frequentes' },
  { code: 'lack_of_empathy', label: 'Falta de empatia' },
  { code: 'slow_response', label: 'Resposta lenta' },
  { code: 'script_deviation', label: 'Desvio de roteiro' },
  { code: 'aggressive_tone', label: 'Tom agressivo' },
  { code: 'inappropriate_humor', label: 'Humor inadequado' },
  { code: 'lack_of_validation', label: 'Falta de validação' },
];

type TypeMap = Record<BehavioralPatternTypeCode, boolean>;

interface FormState {
  enabled: boolean;
  types: TypeMap;
}

function toForm(c: SerializedTenantConfig): FormState {
  const monitored = c.behavioralMonitoredPatternTypes;
  const types = {} as TypeMap;
  for (const { code } of PATTERN_TYPES) {
    // null = todos monitorados (default); array = só os listados.
    types[code] = monitored === null ? true : monitored.includes(code);
  }
  return { enabled: c.behavioralDetectionEnabled, types };
}

/** Subset → payload. Todos marcados ⇒ `null` (future-proof); senão array. */
function buildMonitored(types: TypeMap): BehavioralPatternTypeCode[] | null {
  const selected = PATTERN_TYPES.filter(({ code }) => types[code]).map(({ code }) => code);
  return selected.length === PATTERN_TYPES.length ? null : selected;
}

export function BehavioralTab({ canManage }: { readonly canManage: boolean }): JSX.Element {
  const query = useTenantConfig();
  const update = useUpdateTenantConfig();
  const [form, setForm] = useState<FormState | null>(null);

  useEffect(() => {
    if (query.data !== undefined) setForm(toForm(query.data));
  }, [query.data]);

  const serverForm = useMemo(
    () => (query.data !== undefined ? toForm(query.data) : null),
    [query.data],
  );

  const isDirty = useMemo(() => {
    if (form === null || serverForm === null) return false;
    return JSON.stringify(form) !== JSON.stringify(serverForm);
  }, [form, serverForm]);

  // Payload a partir de UM form — usado nos dois sentidos: `buildRequest(form)`
  // (o que enviar) e `buildRequest(serverForm)` (valores antigos, para o `undo`,
  // pela MESMA mutation).
  const buildRequest = (f: FormState): UpdateTenantConfigRequest => ({
    behavioralDetectionEnabled: f.enabled,
    behavioralMonitoredPatternTypes: buildMonitored(f.types),
  });

  const changedLabels = useMemo(() => {
    if (form === null || serverForm === null) return [];
    const labels: string[] = [];
    if (form.enabled !== serverForm.enabled) {
      labels.push(BEHAVIORAL_TAB_FIELD_LABELS.behavioralDetectionEnabled);
    }
    if (JSON.stringify(form.types) !== JSON.stringify(serverForm.types)) {
      labels.push(BEHAVIORAL_TAB_FIELD_LABELS.behavioralMonitoredPatternTypes);
    }
    return labels;
  }, [form, serverForm]);

  const previousValues = useRef<UpdateTenantConfigRequest | null>(null);

  const handleDiscard = (): void => {
    if (serverForm !== null) setForm(serverForm);
    update.reset();
  };

  // Precisa ser chamado incondicionalmente em todo render (regra dos hooks) —
  // antes dos returns condicionais de loading/erro abaixo.
  useSectionSave(
    isDirty && canManage && form !== null && serverForm !== null
      ? {
          sectionId: 'comportamental',
          changedLabels,
          save: async () => {
            previousValues.current = buildRequest(serverForm);
            await update.mutateAsync(buildRequest(form));
          },
          discard: handleDiscard,
          undo: async () => {
            if (previousValues.current !== null) {
              await update.mutateAsync(previousValues.current);
            }
          },
        }
      : null,
  );

  if (query.isLoading) {
    return <p className="text-sm text-text-secondary">Carregando configurações…</p>;
  }
  if (query.isError || query.data === undefined || form === null) {
    return <p className="text-sm text-red-600">Falha ao carregar as configurações do tenant.</p>;
  }

  const fieldsDisabled = !canManage || update.isPending;
  const selectedCount = PATTERN_TYPES.filter(({ code }) => form.types[code]).length;

  const setType = (code: BehavioralPatternTypeCode, value: boolean): void => {
    setForm((f) => (f === null ? f : { ...f, types: { ...f.types, [code]: value } }));
  };

  return (
    <div className="max-w-2xl">
      {/* Header próprio saiu (fix round 1, T8) — o essencial ("sinaliza ao
          supervisor") foi dobrado no `item.efeito` do catálogo (`catalog.ts`);
          DomainSection supre título + descrição quando esta aba é composta no hub. */}
      <div className="space-y-6">
        {/* Master switch */}
        <div className="flex items-center justify-between rounded-xl border border-border-subtle bg-surface p-4">
          <div>
            <h3 className="text-sm font-medium text-text-primary">Detecção ativa</h3>
            <p className="mt-0.5 text-xs text-text-secondary">
              {detectionStatusCopy(form.enabled)}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.enabled}
            aria-label="Detecção comportamental ativa"
            disabled={fieldsDisabled}
            onClick={() => {
              setForm((f) => (f === null ? f : { ...f, enabled: !f.enabled }));
            }}
            className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 ${
              form.enabled ? 'bg-accent' : 'bg-border-subtle'
            }`}
          >
            <span
              className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
                form.enabled ? 'translate-x-[18px]' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {/* Tipos monitorados */}
        <div
          className={`rounded-xl border border-border-subtle bg-surface p-4 ${
            form.enabled ? '' : 'opacity-50'
          }`}
        >
          <h3 className="text-sm font-medium text-text-primary">Padrões monitorados</h3>
          <p className="mt-1 mb-3 text-xs text-text-secondary">
            {selectedCount === PATTERN_TYPES.length
              ? 'Todos os padrões monitorados (novos tipos entram automaticamente).'
              : `${selectedCount.toString()} de ${PATTERN_TYPES.length.toString()} padrões monitorados.`}
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {PATTERN_TYPES.map(({ code, label }) => (
              <label
                key={code}
                className={`flex items-center gap-2 rounded-lg border border-border-subtle px-3 py-2 text-sm ${
                  fieldsDisabled || !form.enabled
                    ? 'cursor-not-allowed text-text-secondary'
                    : 'cursor-pointer text-text-primary hover:bg-app-bg'
                }`}
              >
                <input
                  type="checkbox"
                  checked={form.types[code]}
                  disabled={fieldsDisabled || !form.enabled}
                  onChange={(e) => {
                    setType(code, e.target.checked);
                  }}
                  className="h-4 w-4 rounded border-border-subtle text-accent focus:ring-accent disabled:opacity-50"
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Descartar/Salvar saíram — vivem na StickySaveBar do domínio (Admin Hub
          T8); sucesso/erro do save viram toast da barra, não mensagem local. */}
      {!canManage && (
        <p className="mt-6 rounded-lg border border-border-subtle bg-app-bg px-4 py-3 text-sm text-text-secondary">
          Você tem acesso de leitura. A edição destas configurações é restrita ao administrador do
          tenant.
        </p>
      )}
    </div>
  );
}
